/**
 * Source-value extraction + transform application for modulation bindings.
 *
 * Each transform takes the raw source value, an optional `rate`, and the
 * current time/dt, and returns a scalar contribution-before-amount.
 */

import type { AudioFrame } from '../../audio/analysis/analyser';
import type { AudioSource, TransformKind } from '../presets/presetTypes';

const TWO_PI = Math.PI * 2;

export function readSource(source: AudioSource, frame: AudioFrame): number {
  switch (source) {
    case 'time':
      return frame.time;
    case 'rms':
      return frame.rms;
    case 'bass':
      return frame.bass;
    case 'mid':
      return frame.mid;
    case 'treble':
      return frame.treble;
    case 'beatConfidence':
      return frame.beat.confidence;
  }
}

export type TransformContext = {
  rate: number;
};

export function applyTransform(
  kind: TransformKind,
  value: number,
  ctx: TransformContext,
): number {
  switch (kind) {
    case 'linear':
      return value;
    case 'sine':
      // sin(value * 2pi * rate). With source=time and rate=0.1 -> 0.1 Hz.
      return Math.sin(value * TWO_PI * ctx.rate);
    case 'smoothPulse': {
      const v = clamp01(value);
      return v * v * (3 - 2 * v);
    }
    case 'expFalloff':
      return Math.exp(-Math.max(0, ctx.rate) * Math.max(0, value));
  }
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

/**
 * Per-binding low-pass smoothing. `smoothingMs` is the time constant: bigger
 * = slower response. dt is in seconds.
 */
export function smoothValue(
  prev: number,
  next: number,
  smoothingMs: number,
  dt: number,
): number {
  if (smoothingMs <= 0 || dt <= 0) return next;
  // Frame-rate-independent exponential smoothing: alpha = 1 - exp(-dt / tau).
  const tau = smoothingMs / 1000;
  const alpha = 1 - Math.exp(-dt / tau);
  return prev + (next - prev) * alpha;
}
